import React, { Children, memo } from 'react';
import {
  Input,
  DatePicker,
  Switch,
  InputNumber,
  Radio,
  Checkbox,
  Tag,
} from '@bedrock/components';
import { ProTable } from '@bedrock/eed-react';
import classNames from 'classnames';
import PureText from './PureText';
import './index.less';
import TextAreaPopover from '../SForm/SFormElement/TextAreaPropover';
import TextAreaWithUpload from '#components/SForm/SFormElement/TextAreaWithUpload';
import CheckboxWithTextArea from '#components/SForm/SFormElement/CheckboxWithTextArea';


function valueToArray(value: any) {
  if (Array.isArray(value)) return value;
  return [value];
}

const defaultProps = {
  className: 'mpc-quote-filed',
};

const TextAreaPopoverInput = (props) => {
  return (
    <TextAreaPopover
      renderChildren={(value) => {
        return (
          <Input
            value={value}
            size="small"
            {...props.inputProps}
          />
        );
      }}
      {...props}
      view={false}
    />
  );
}


/**
 * 单元格编辑组件高阶函数
 * 
 * 用于包装受控组件，解决在 ProTable 编辑场景下因 onChange 触发导致的不必要重渲染问题。
 * 通过 React.memo 和自定义比较函数，确保只有在关键 props（如 value）变化时才重新渲染组件。
 * 
 * @template P - 组件 props 类型
 * @param {React.ComponentType<P>} Component - 需要包装的受控组件
 * @returns {React.MemoExoticComponent<React.ComponentType<P>>} 记忆化后的组件
 * 
 * @example
 * ```tsx
 * const MemoizedInput = cellEditMemo(Input);
 * // 在 ProTable 的 editRender 中使用
 * component: MemoizedInput
 * ```
 * 
 * @remarks
 * - 使用浅比较（shallow comparison）比较 props
 * - 特别关注 value、checked 等受控属性，确保值变化时组件能正确更新
 * - onChange 等函数引用变化不会触发重渲染（通过自定义比较函数控制）
 * - 适用于 Input、Switch、Checkbox、Radio 等受控组件
 */
function cellEditMemo<P extends Record<string, any>>(
  Component: React.ComponentType<P>
): React.MemoExoticComponent<React.ComponentType<P>> {
  const LocalComponent: React.FC<P> = (props) => {
    return <Component size="small" {...props} />;
  };

  LocalComponent.displayName = `cellEditMemo(${Component.displayName || Component.name || 'Component'})`;

  return memo(LocalComponent, (prevProps, nextProps) => {
    // 获取所有受控属性的 key（常见的受控属性）
    const controlledKeys = ['value', 'checked', 'defaultValue', 'defaultChecked'];
    
    // 检查受控属性是否发生变化
    for (const key of controlledKeys) {
      if (key in prevProps || key in nextProps) {
        if (prevProps[key] !== nextProps[key]) {
          return false; // 受控属性变化，需要重新渲染
        }
      }
    }

    // 浅比较其他 props
    const prevKeys = Object.keys(prevProps);
    const nextKeys = Object.keys(nextProps);
    
    if (prevKeys.length !== nextKeys.length) {
      return false; // props 数量不同，需要重新渲染
    }

    // 比较每个 prop 的值（排除函数类型的 prop，因为函数引用变化不应该触发重渲染）
    for (const key of prevKeys) {
      // 跳过受控属性（已在上面检查过）
      if (controlledKeys.includes(key)) {
        continue;
      }
      
      // 跳过函数类型的 prop（onChange、onClick 等）
      if (typeof prevProps[key] === 'function' && typeof nextProps[key] === 'function') {
        continue;
      }
      
      // 比较其他类型的 prop
      if (prevProps[key] !== nextProps[key]) {
        return false; // prop 值变化，需要重新渲染
      }
    }

    return true; // props 没有实质性变化，不需要重新渲染
  }) as React.MemoExoticComponent<React.ComponentType<P>>;
}


const CellTextArea = cellEditMemo(Input.TextArea);

const CellMPCInputNumber = cellEditMemo(InputNumber);


ProTable.addEditRender(
  'mpc-textArea',
  ({ props, setRow, toggleEditableRowKey, getRowKey }, editable) => ({
    component: editable
      ? TextAreaPopoverInput
      : (props) => (
          <div
            onClick={() => {
              toggleEditableRowKey?.(
                getRowKey?.(props?.row) as string,
                editable
              );
            }}
          >
            <TextAreaPopover {...props} view="view" />
          </div>
        ),
    props: {
      ...props,
      onChange(e, row, column, rowIndex, ...args) {
        const changedRow = {
          ...row,
          [column.dataIndex as any]: e.target.value,
        };
        setRow(changedRow);
        props?.onChange?.(e, changedRow, column, rowIndex, ...args);
      },
    },
  })
);

const CellMPCRender = cellEditMemo(({ customComp, ...rest }) => {
  const Comp = customComp;
  return <Comp {...rest} />;
});

ProTable.addEditRender(
  'custom-component',
  ({ props, setRow, toggleEditableRowKey, getRowKey }, editable) => {
    return {
      component: CellMPCRender,
      props: {
        ...props,
        onChange(e, row, column, rowIndex, ...args) {
          const changedRow = {
            ...row,
            [column.dataIndex as any]: e.target.value,
          };
          setRow(changedRow);
          props?.onChange?.(e, changedRow, column, rowIndex, ...args);
        },
        setRow,
        editable,
      },
    };
  }
);

ProTable.addEditRender(
  'switch-quote',
  ({ props, setRow, toggleEditableRowKey, getRowKey }, editable) => {
    return {
      component: editable
        ? Switch
        : (props) => (
            <div
              onClick={() => {
                toggleEditableRowKey?.(
                  getRowKey?.(props.row) as string,
                  editable
                );
              }}
            >
              <Switch {...props} disabled />
            </div>
          ),
      props: {
        ...props,
        onChange(e, row, column, rowIndex, ...args) {
          const changedRow = {
            ...row,
            [column.dataIndex as any]: e.target.value,
          };
          setRow(changedRow);
          props?.onChange?.(e, changedRow, column, rowIndex, ...args);
        },
      },
    };
  }
);

ProTable.addEditRender(
  'single-radio',
  ({ props, setRow, toggleEditableRowKey, getRowKey }, editable) => {
    return {
      component: editable
        ? ({ children, value, ...rest }) => {
            return (
              <Radio checked={value} {...rest}>
                {props.children}
              </Radio>
            );
          }
        : ({ children, value, ...rest }) => (
            <div
              onClick={() => {
                toggleEditableRowKey?.(
                  getRowKey?.(props.row) as string,
                  editable
                );
              }}
            >
              <Radio checked={value} {...rest} disabled>
                {props.children}
              </Radio>
            </div>
          ),
      props: {
        ...props,
        onChange(e, row, column, rowIndex, ...args) {
          const changedRow = {
            ...row,
            [column.dataIndex as any]: e.target.checked,
          };
          setRow(changedRow);
          props?.onChange?.(e, changedRow, column, rowIndex, ...args);
        },
      },
    };
  }
);

ProTable.addEditRender(
  'single-checkbox',
  ({ props, setRow, toggleEditableRowKey, getRowKey }, editable) => {
    return {
      component: ({ children, value, ...rest }) => {
        return (
          <Checkbox checked={value} {...rest}>
            {props.children}
          </Checkbox>
        );
      },

      props: {
        ...props,
        onChange(e, row, column, rowIndex, ...args) {
          const changedRow = {
            ...row,
            [column.dataIndex as any]: e.target.checked,
          };
          setRow(changedRow);
          props?.onChange?.(e, changedRow, column, rowIndex, ...args);
        },
      },
    };
  }
);

ProTable.addEditRender(
  'text_with_upload',
  ({ props, setRow, toggleEditableRowKey, getRowKey }, editable) => {
    return {
      component: TextAreaWithUpload,
      props: {
        ...props,
        view: !editable,
        onChange(e, row, column, rowIndex, ...args) {
          const changedRow = {
            ...row,
            [column.dataIndex as any]: e.target.value,
          };
          setRow(changedRow);
          props?.onChange?.(e, changedRow, column, rowIndex, ...args);
        },
      },
    };
  }
);


ProTable.addEditRender("inputTextarea", ({ props, setRow, toggleEditableRowKey, getRowKey }, editable) => ({
  component: editable
    ? CellTextArea
    : (props) => (
      <div
        onClick={() => {
          toggleEditableRowKey?.(getRowKey?.(props?.row) as string, true)
        }}
      >
        <PureText {...props} />
      </div>
    ),
  props: {
    ...props,
    onChange(e, row, column, rowIndex, ...args) {
      const changedRow = {
        ...row,
        [column.dataIndex as any]: e.target.value,
      }
      setRow(changedRow)
      props?.onChange?.(e, changedRow, column, rowIndex, ...args)
    },
  },
}))

ProTable.addEditRender("MPCInputNumber", ({ props, setRow, toggleEditableRowKey, getRowKey }, editable) => ({
  component: editable
    ? CellMPCInputNumber
    : (props) => (
      <div
        onClick={() => {
          toggleEditableRowKey?.(getRowKey?.(props?.row) as string, true)
        }}
      >
        <PureText {...props} />
      </div>
    ),
  props: {
    ...props,
    onChange(e, row, column, rowIndex, ...args) {
      const changedRow = {
        ...row,
        [column.dataIndex as any]: e.target.value,
      }
      setRow(changedRow)
      props?.onChange?.(e, changedRow, column, rowIndex, ...args)
    },
  },
}))



// @ts-ignore
ProTable?.addBatchEditRender?.('inputNumber', ({ props }) => ({
  component: InputNumber,
  props: {
    ...props,
    onChange(e, column, rowIndex, ...args) {
      props?.onChange?.(e, column, rowIndex, ...args);
    },
  },
}));

ProTable?.addBatchEditRender?.('dateRange', ({ props }) => ({
  component: DatePicker,
  props: {
    range: true,
    ...props,
    onChange(e, column, rowIndex, ...args) {
      props?.onChange?.(e, column, rowIndex, ...args);
    },
  },
}));

ProTable.addEditRender(
  'radio-group',
  ({ props, setRow, toggleEditableRowKey, getRowKey }, editable) => {
    return {
      component: Radio.Group,
      props: {
        ...props,
        onChange(e, row, column, rowIndex, ...args) {
          const changedRow = {
            ...row,
            [column.dataIndex as any]: e.target.value,
          };
          setRow(changedRow);
          props?.onChange?.(e, changedRow, column, rowIndex, ...args);
        },
        className: `${props.className} mpc-pro-table-radio-group`,
      },
    };
  }
);

ProTable.addEditRender(
  'checkbox-with-text-area',
  ({ props, setRow, toggleEditableRowKey, getRowKey }, editable) => {
    return {
      component: CheckboxWithTextArea,
      props: {
        ...props,
        onChange(e, row, column, rowIndex, ...args) {
          const changedRow = {
            ...row,
            [column.dataIndex as any]: e.target.value,
          };
          setRow(changedRow);
          props?.onChange?.(e, changedRow, column, rowIndex, ...args);
        },
      },
    };
  }
);
